import { Component, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FuseConfirmationConfig } from '../confirmation.types';
import { NgClass } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { NativeDateAdapter } from '@angular/material/core';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'fuse-confirmation-dialog',
    templateUrl: './dialog.component.html',
    styleUrls: ['./dialog.component.css'],
    encapsulation: ViewEncapsulation.None, // This is important for styling to apply globally
    standalone: true,
    imports: [
        MatButtonModule, 
        MatDialogModule, 
        MatIconModule, 
        NgClass, 
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatNativeDateModule,
        DecimalPipe
    ],
    providers: [
        { provide: DateAdapter, useClass: NativeDateAdapter },
        { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
        { 
            provide: MAT_DATE_FORMATS, 
            useValue: {
                parse: {
                    dateInput: 'MM/DD/YYYY',
                },
                display: {
                    dateInput: 'MM/DD/YYYY',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            }
        }
    ]
})
export class FuseConfirmationDialogComponent {
    data: FuseConfirmationConfig = inject(MAT_DIALOG_DATA);
    dialogRef = inject(MatDialogRef<FuseConfirmationDialogComponent>);

    constructor() {
        // Initialize selected date if not provided
        if (this.data.showDatePicker && !this.data.selectedDate) {
            this.data.selectedDate = new Date();
        }
        
        // Initialize amount if not provided but showAmount is true
        if (this.data.showAmount && this.data.amount === undefined) {
            this.data.amount = 0;
        }
        
        // Calculate initial amount if needed
        this.calculateAmount();
    }
    
    /**
     * Handle date change event
     */
    onDateChange(): void {
        this.calculateAmount();
    }
    
    /**
     * Calculate the amount based on the selected date
     */
    private calculateAmount(): void {
        // Make sure showAmount is properly initialized
        if (this.data.showAmount === undefined) {
            this.data.showAmount = false;
        }
        
        // If showAmount is true and calculateAmount is provided, calculate the amount
        if (this.data.showAmount && this.data.calculateAmount && this.data.selectedDate) {
            try {
                this.data.amount = this.data.calculateAmount(this.data.selectedDate);
                // Ensure amount is a number
                if (isNaN(this.data.amount)) {
                    this.data.amount = 0;
                }
            } catch (error) {
                console.error('Error calculating amount:', error);
                this.data.amount = 0;
            }
        } else if (this.data.showAmount && this.data.amount === undefined) {
            // If showAmount is true but no amount is provided, set it to 0
            this.data.amount = 0;
        }
    }

    confirm(): void {
        // Use the legacy format (just a string) if no date picker or amount is shown
        // This maintains backward compatibility with existing code
        if (!this.data.showDatePicker && !this.data.showAmount) {
            this.dialogRef.close('confirmed');
        } else {
            // Use the object format with result, selectedDate and amount if needed
            this.dialogRef.close({
                result: 'confirmed',
                selectedDate: this.data.selectedDate,
                amount: this.data.amount
            });
        }
    }
}
